import { Customer, CustomerChange } from '../types';
import { diffCustomerChanges } from './customerChangeLog';
import { composePersonName, parsePersonName } from './personName';

/**
 * Kundenstamm aus einem offenen Beleg heraus bearbeiten.
 *
 * Die Regeln der Schnellbearbeitung liegen hier und nicht in der Maske, weil
 * das Projekt keine DOM-Testumgebung hat: alles, was in der Komponente steht,
 * ist unpruefbar. Geprueft werden muss vor allem die Zusage, die der Dialog dem
 * Nutzer gibt — welcher Beleg die Aenderung uebernimmt und welcher nicht.
 *
 * Die Feldauswahl folgt dem Andruck: Rechnung und Angebot drucken aus dem
 * Kundenstamm ausschliesslich name/name2/name3, street/zip/city (ersatzweise
 * das Alt-Feld address) und customerNumber (generatorInvoice.ts, generatorQuote.ts).
 * E-Mail und Telefon kommen dazu, weil der Versand an ihnen haengt.
 */

export type DocumentKind = 'invoice' | 'quote';

/**
 * Wie dieser Beleg auf eine Stammdatenaenderung reagiert.
 *
 * Bewusst DREI Zustaende statt "festgeschrieben ja/nein": abgeschlossen und
 * eingefroren sind nicht dasselbe. Der Snapshot entsteht an genau einer Stelle
 * (Rechnung beim Ausstellen, Angebot beim Versand) — ein Angebot, das aus dem
 * Entwurf heraus direkt auf "angenommen" gesetzt wurde, ist gesperrt und hat
 * trotzdem keinen. Ihm eine Festschreibung zuzusagen waere schlicht unwahr:
 * seine Vorschau und jedes weitere PDF traegt die neue Anschrift.
 */
export type DocumentCustomerState =
    /** Beleg in Bearbeitung — die Aenderung landet darin. */
    | 'open'
    /** Beleg abgeschlossen UND mit eingefrorener Anschrift. */
    | 'frozen'
    /** Beleg abgeschlossen, aber ohne eingefrorene Anschrift. */
    | 'unfrozen';

/**
 * `open` = der Beleg darf noch bearbeitet werden.
 * `frozenAddress` = es gibt einen Kunden-Snapshot bzw. ein Archivdokument, aus
 * dem der Beleg seine Anschrift nimmt.
 */
export const documentCustomerState = (open: boolean, frozenAddress: boolean): DocumentCustomerState =>
    open ? 'open' : frozenAddress ? 'frozen' : 'unfrozen';

/**
 * Vor- und Nachname zur Anzeige. Leere Felder werden AUS DEM NAMEN abgeleitet
 * (Altbestand kennt die Zerlegung nicht) — abgeleitet, nicht gespeichert:
 * solange niemand tippt, entsteht keine Aenderung. Gleiche Regel wie in
 * CustomerDetail, damit beide Masken denselben Kunden gleich zeigen.
 */
export const personNameParts = (c: Pick<Customer, 'firstName' | 'lastName' | 'name'>): {
    firstName: string;
    lastName: string;
} => {
    const first = (c.firstName ?? '').trim();
    const last = (c.lastName ?? '').trim();
    if (first || last) return { firstName: first, lastName: last };
    const parsed = parsePersonName(c.name ?? '');
    return { firstName: parsed.firstName ?? '', lastName: parsed.lastName ?? '' };
};

/**
 * Einen Namensteil setzen und den Vollnamen mitfuehren.
 *
 * `name` bleibt ueberall die Anzeige (PDF, Angebot, Rechnung, Suche); im
 * Personen-Modus zeigt die Maske das Feld gar nicht, es MUSS also hier
 * entstehen. Der Titel gehoert nicht hinein.
 */
export const withPersonNamePart = (
    c: Customer,
    key: 'firstName' | 'lastName',
    value: string,
): Customer => {
    const parts = personNameParts(c);
    const next: Customer = { ...c, firstName: parts.firstName, lastName: parts.lastName, [key]: value };
    next.name = composePersonName(next.firstName, next.lastName);
    return next;
};

/**
 * Das Alt-Feld `address` aus Strasse/PLZ/Ort nachfuehren.
 *
 * Beide PDF-Generatoren drucken die strukturierten Felder und fallen nur auf
 * `address` zurueck, wenn alle drei leer sind. Ein Kunde, dessen Anschrift hier
 * gepflegt wird, darf daneben keine widersprechende Alt-Anschrift behalten —
 * sonst druckt ein anderer Pfad (DATEV, Alt-Auswertung) weiter die alte.
 * Bleiben alle drei leer, bleibt `address` unangetastet.
 *
 * WANN nachgezogen wird, entscheidet der Aufrufer (planCustomerUpdate) — nur
 * bei einer echten Aenderung an Strasse, PLZ oder Ort.
 */
export const composeCustomerAddress = (
    c: Pick<Customer, 'street' | 'zip' | 'city' | 'address'>,
): string | undefined => {
    if (!c.street && !c.zip && !c.city) return c.address;
    return `${c.street || ''}, ${c.zip || ''} ${c.city || ''}`
        .replace(/\s+/g, ' ')
        .replace(/^[,\s]+|[,\s]+$/g, '')
        .trim();
};

/** Getrimmter Textwert; alles Nicht-Textliche zaehlt als leer. */
const blank = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/** Leerer Text, null oder undefined — fuer den Vergleich derselbe Zustand. */
const isBlankish = (v: unknown): boolean =>
    v === undefined || v === null || (typeof v === 'string' && v.trim() === '');

export interface CustomerUpdatePlan {
    /** Der vollstaendige neue Kunde — das, was der Beleg danach anzeigt. */
    toSave: Customer;
    /** Nur die tatsaechlich geaenderten Spalten. */
    updates: Partial<Customer>;
    /** Historien-Eintraege fuer changeLog (leer = nichts zu historisieren). */
    changes: CustomerChange[];
}

/**
 * Was gespeichert werden muss.
 *
 * `before` ist der Stand, den der Dialog geladen hat — NICHT der Kunde aus der
 * Belegliste: der kann veraltet oder (in der Angebotsliste) ein Platzhalter mit
 * nur einem Namen sein, und gegen den zu diffen schriebe leere Felder ueber
 * gepflegte. Ein leeres `updates` heisst: nichts anfassen.
 */
export const planCustomerUpdate = (before: Customer, after: Customer): CustomerUpdatePlan => {
    // Das Alt-Feld wird NUR nachgezogen, wenn jemand tatsaechlich an Strasse,
    // PLZ oder Ort war. Bedingungslos zusammengesetzt (so stand es in
    // CustomerDetail) weicht es bei fast jedem Bestandskunden vom gespeicherten
    // Wert ab — mehrzeilige Alt-Anschriften, c/o-Zeilen, Laenderangaben. Der
    // Dialog waere dann schon vor der ersten Eingabe "geaendert", und wer nur
    // die E-Mail korrigiert, loeschte nebenbei die Zusaetze und erzeugte einen
    // Historieneintrag "Adresse", den er nie vorgenommen hat.
    //
    // Verglichen wird getrimmt, wie in diffCustomerChanges: ein angehaengtes
    // Leerzeichen ist keine Anschriftsaenderung und darf die Alt-Anschrift
    // nicht wegschreiben.
    const addressTouched = (['street', 'zip', 'city'] as const).some(
        (k) => blank(before[k]) !== blank(after[k]),
    );
    const toSave: Customer = addressTouched
        ? { ...after, address: composeCustomerAddress(after) }
        : { ...after };
    const updates: Partial<Customer> = {};
    (Object.keys(toSave) as (keyof Customer)[]).forEach((k) => {
        // Leer bleibt leer: null, undefined und '' sind derselbe Zustand. Ohne
        // diese Ausnahme schriebe schon das Anfassen und Wiederleeren eines
        // Feldes ein '' in eine bis dahin leere Spalte — eine Aenderung ohne
        // Inhalt, die die Historie (diffCustomerChanges) zurecht nicht kennt.
        if (isBlankish(toSave[k]) && isBlankish(before[k])) return;
        if (JSON.stringify(toSave[k]) !== JSON.stringify(before[k])) {
            (updates as Record<string, unknown>)[k] = toSave[k];
        }
    });
    return { toSave, updates, changes: diffCustomerChanges(before, toSave) };
};

/**
 * Blockierende Eingabefehler. Der Name ist das einzige Pflichtfeld — er steht
 * im Anschriftfeld jedes Belegs, und ein Kunde ohne Namen erzeugt ein PDF mit
 * leerem Empfaenger.
 */
export const validateCustomerQuickEdit = (c: Customer): string[] => {
    const out: string[] = [];
    if (!(c.name ?? '').trim()) {
        out.push(
            (c.customerType ?? 'company') === 'person'
                ? 'Nachname fehlt — er bildet den Namen im Anschriftfeld.'
                : 'Name fehlt — er steht im Anschriftfeld des Belegs.',
        );
    }
    return out;
};

/**
 * Der Satz, den der Dialog ueber DIESEN Beleg sagt.
 *
 * Ohne ihn liest sich ein festgeschriebener Beleg, der sich nach dem Speichern
 * nicht ruehrt, wie ein Fehler. Die Festschreibung ist gewollt (Rechnung:
 * customer_snapshot beim Ausstellen, Datenbank-Trigger; Angebot: Snapshot beim
 * Versand) und wird hier ausgesprochen statt umgangen.
 */
export const customerEditNotice = (kind: DocumentKind, state: DocumentCustomerState): string => {
    if (state === 'frozen') {
        return kind === 'invoice'
            ? 'Diese Rechnung ist festgeschrieben: Sie behält die Anschrift, mit der sie ausgestellt wurde. Die Änderung wirkt nur im Kundenstamm und auf künftige Belege — dieser Beleg bleibt unverändert.'
            : 'Dieses Angebot ist versendet: Es behält die Anschrift, mit der es verschickt wurde. Die Änderung wirkt nur im Kundenstamm und auf künftige Angebote — dieses Angebot bleibt unverändert.';
    }
    if (state === 'unfrozen') {
        return kind === 'invoice'
            ? 'Achtung: Diese Rechnung ist nicht mehr in Bearbeitung, hat aber keine festgeschriebene Anschrift (Beleg aus der Zeit davor). Die Änderung schlägt deshalb auch hier durch — der Beleg bleibt NICHT unverändert.'
            : 'Achtung: Dieses Angebot ist nicht mehr in Bearbeitung, hat aber keine eingefrorene Anschrift — es wurde nie versendet oder stammt aus der Zeit davor. Die Änderung schlägt deshalb auch hier durch — das Angebot bleibt NICHT unverändert.';
    }
    return kind === 'invoice'
        ? 'Die Änderung wird sofort in diesen Rechnungsentwurf übernommen.'
        : 'Die Änderung wird sofort in dieses Angebot übernommen.';
};

/**
 * Welcher Kundenstand fuer diesen Beleg gilt.
 *
 * Festgeschrieben gewinnt der Snapshot, sonst der lebende Kunde — dieselbe
 * Regel, nach der beide PDF-Vorschauen rendern. Damit zeigt die Kopfzeile
 * niemals einen anderen Namen als das Dokument darunter.
 */
export const resolveDocumentCustomer = <T extends Customer>(
    live: T | undefined,
    snapshot: T | undefined,
    frozen: boolean,
): T | undefined => (frozen && snapshot ? snapshot : live ?? snapshot);

/**
 * Der Kundenname in der Kopfzeile eines Belegs.
 *
 * `fallback` ist der am Beleg mitgelesene Name (Join auf customers) — er greift
 * nur, wenn der Kunde gar nicht geladen ist. Waere er wie bisher der erste
 * Kandidat, zeigte die Kopfzeile nach einer Umbenennung noch den alten Namen,
 * waehrend das PDF daneben schon den neuen druckt.
 */
export const documentCustomerName = (
    live: Customer | undefined,
    snapshot: Customer | undefined,
    frozen: boolean,
    fallback?: string,
): string => {
    const c = resolveDocumentCustomer(live, snapshot, frozen);
    return (c?.name ?? '').trim() || (fallback ?? '').trim() || 'Kunde unbekannt';
};

/**
 * Die Alt-Anschrift, die dem Bearbeiter noch angeboten werden soll.
 *
 * Altbestand traegt die Anschrift nur im Freitextfeld `address`. Wer im
 * Schnelldialog dann bloss die Strasse eintippt, laesst PLZ und Ort leer — und
 * die Neu-Komposition macht aus "Hauptstr. 1, 12345 Berlin" ein blosses
 * "Nebenweg 5". Solange also noch ein strukturiertes Feld fehlt, bleibt das
 * Angebot "uebernehmen" stehen; CustomerDetail blendet es schon beim ersten
 * Tastendruck aus, was genau diese Luecke offenlaesst.
 *
 * `null` = nichts anzubieten.
 */
export const legacyAddressHint = (
    loaded: Pick<Customer, 'address'>,
    draft: Pick<Customer, 'street' | 'zip' | 'city'>,
): string | null => {
    const alt = blank(loaded.address);
    if (!alt) return null;
    const vollstaendig = Boolean(blank(draft.street) && blank(draft.zip) && blank(draft.city));
    return vollstaendig ? null : alt;
};

/**
 * Die Alt-Anschrift in die strukturierten Felder uebernehmen — ohne bereits
 * Getipptes zu ueberschreiben.
 */
export const applyLegacyAddress = <T extends Pick<Customer, 'street' | 'zip' | 'city'>>(
    draft: T,
    parsed: { street?: string; zip?: string; city?: string },
): T => {
    const fill = (current: string | undefined, fromLegacy: string | undefined) =>
        isBlankish(current) ? (fromLegacy ?? current) : current;
    return {
        ...draft,
        street: fill(draft.street, parsed.street),
        zip: fill(draft.zip, parsed.zip),
        city: fill(draft.city, parsed.city),
    };
};

/**
 * Den geaenderten Kunden in eine geladene Liste zurueckspielen.
 *
 * Ein bekannter Kunde wird ersetzt. Ein unbekannter wird nur angehaengt, wenn
 * er NICHT archiviert ist: fetchCustomers blendet archivierte Kunden aus, und
 * ueber diesen Rueckweg staende ein archivierter Kunde sonst ploetzlich wieder
 * in jeder Auswahlliste — angelegt durch das blosse Ansehen seiner Stammdaten.
 */
export const mergeCustomerIntoList = <T extends Pick<Customer, 'id' | 'archivedAt'>>(
    list: readonly T[],
    updated: T,
): T[] => {
    if (list.some((c) => c.id === updated.id)) {
        return list.map((c) => (c.id === updated.id ? updated : c));
    }
    return updated.archivedAt ? [...list] : [...list, updated];
};
